import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {InstallationApiService} from '../../services/installation-api.service';
import {ClampDefinitionService} from '../../services/clamp-definition.service';
import {LiveDataService} from '../../services/live-data.service';
import {
  getRegisterFlags, getRegisterTypeDisplayClass,
  getRegisterTypeId, getRegisterTypeLabel,
  getRegisterTypes, RegisterTypeEnum,
  Hub,
  Register
} from "../../models/hub.model";
import {interval, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ClampDefinition} from "../../models/clamp-definition.model";

@Component({
  selector: 'app-hub-configuration',
  templateUrl: './hub-configuration.component.html',
  styleUrls: ['./hub-configuration.component.scss']
})
export class HubConfigurationComponent implements OnInit, OnChanges, OnDestroy {
  @Input() hubId: string = '';

  hubConfiguration: Hub|null = null;
  loading: boolean = false;
  error: string = '';
  clampDefinitionsLoading: boolean = false;
  clampDefinitionsError: string = '';

  avgE:number = 0;
  sumE:number = 0;
  sumP:number = 0;

  // WebSocket related properties
  wsConnected: boolean = false;
  wsError: string = '';
  wsData: any = null;
  lastDataReceivedTime: number | null = null;
  connectionStatus: 'connected' | 'warning' | 'danger' = 'connected';
  showRawLiveData: boolean = false; // Property to control visibility of Raw Live Data
  private destroy$ = new Subject<void>();

  // Register editing properties
  editingRegisterIndex: number | null = null;
  editingRegisterLabel: string = '';

  // Full register editing properties
  editMode: boolean = false;
  editingRegister: Register | null = null;
  savingRegister: boolean = false;
  registerError: string = '';

  // Temporary values for editing
  editingCircuitType: number = 0;

  editingClampDefinition: string = '';
  editingMultiplier: number = 1;
  editingInverted: boolean = false;

  constructor(
    private installationService: InstallationApiService,
    public clampDefinitionService: ClampDefinitionService,
    private liveDataService: LiveDataService
  ) { }

  ngOnInit(): void {
    // Initialize clamp definitions if not already loaded
    this.initializeClampDefinitions();

    // Subscribe to WebSocket connection status
    this.liveDataService.connectionStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe(connected => {
        this.wsConnected = connected;
        console.log('WebSocket connection status:', connected);
      });

    // Subscribe to WebSocket errors
    this.liveDataService.error$
      .pipe(takeUntil(this.destroy$))
      .subscribe(error => {
        if (error) {
          this.wsError = error;
          console.error('WebSocket error:', error);
        } else {
          this.wsError = '';
        }
      });

    // Subscribe to WebSocket messages
    this.liveDataService.message$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        if (data) {
          this.wsData = data;

          let numChans = 0;


          this.sumE = 0;
          this.sumP = 0;
          let localSumE = 0;
          let localSumP = 0;
          let maxE:number = 0;
          let minE = 0;


          var z1 = Object.keys(data).filter(function(k) {
            return k.indexOf('e') == 0;
          }).reduce(function(newData:any, k) {
            let eVal = data[k];
            if(eVal > maxE) maxE = eVal;
            if(eVal < minE) minE = eVal;
            newData[k] = eVal;
            numChans++;
            localSumE+=eVal;
            return {newData:newData, maxE:maxE, minE:minE, sumE:localSumE};
          }, {});

          this.avgE = this.sumE/numChans;
          this.sumE = localSumE;
          console.log('sumE',this.sumE);

          var z2 = Object.keys(data).filter(function(k) {
            return k.indexOf('p') == 0;
          }).reduce(function(newData:any, k) {
            let pVal = data[k];
            newData[k] = pVal;
            localSumP+=pVal;
            return {newData:newData, sumP:localSumP};
          }, {});

          // this.avgP = this.sumP/numChans;
          this.sumP = localSumP;
          console.log('sumP',this.sumP);


          // Store the timestamp if it exists in the data
          if (data.t) {
            this.lastDataReceivedTime = data.t;
            // Reset connection status to connected when data is received
            this.connectionStatus = 'connected';
          }
          console.log('WebSocket data received:', data);
        }
      });

    // Set up a timer to check the time since the last message was received
    interval(1000) // Check every second
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkLastMessageTime();
      });
  }

  /**
   * Check how long it's been since the last message was received and update the connection status accordingly
   */
  private checkLastMessageTime(): void {
    if (!this.wsConnected || !this.lastDataReceivedTime) {
      return;
    }

    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
    const timeSinceLastMessage = currentTime - this.lastDataReceivedTime;

    if (timeSinceLastMessage > 20) {
      this.connectionStatus = 'danger';
    } else if (timeSinceLastMessage > 10) {
      this.connectionStatus = 'warning';
    } else {
      this.connectionStatus = 'connected';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When hubId changes, fetch the hub configuration
    if (changes['hubId'] && changes['hubId'].currentValue) {
      this.fetchHubConfiguration();

      // Connect to the hub via WebSocket
      this.connectToHubWebSocket();

      // Set lastDataReceivedTime to current Unix epoch time
      this.lastDataReceivedTime = Math.floor(Date.now() / 1000);
    } else if (changes['hubId'] && !changes['hubId'].currentValue) {
      // Clear the hub configuration when hubId is cleared
      this.hubConfiguration = null;
      this.error = '';

      // Disconnect from the WebSocket
      this.liveDataService.disconnect();
    }
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    // Disconnect from the WebSocket
    this.liveDataService.disconnect();
  }

  /**
   * Initialize the clamp definitions lookup table
   */
  initializeClampDefinitions(): void {
    // Only initialize if not already loaded or loading
    if (!this.clampDefinitionService.isLoaded() && !this.clampDefinitionService.isLoading()) {
      this.clampDefinitionsLoading = true;
      this.clampDefinitionsError = '';

      this.clampDefinitionService.initializeClampDefinitions().subscribe({
        next: (success) => {
          this.clampDefinitionsLoading = false;
          if (!success) {
            this.clampDefinitionsError = this.clampDefinitionService.getError();
          }
        },
        error: (err) => {
          this.clampDefinitionsLoading = false;
          this.clampDefinitionsError = `Error initializing clamp definitions: ${err.message || 'Unknown error'}`;
          console.error('Error initializing clamp definitions:', err);
        }
      });
    }
  }

  /**
   * Get the clamp definition label for a given UUID
   * @param uuid The UUID of the clamp definition
   * @returns The label of the clamp definition, or the UUID if not found
   */
  getClampDefinitionLabel(uuid: string): string {
    return this.clampDefinitionService.getClampDefinitionLabel(uuid);
  }

  /**
   * Connect to the hub via WebSocket
   */
  connectToHubWebSocket(): void {
    if (!this.hubId) {
      return;
    }

    console.log(`Connecting to hub ${this.hubId} via WebSocket`);
    this.liveDataService.connectToHub(this.hubId);
  }

  fetchHubConfiguration(): void {
    if (!this.hubId) {
      return;
    }

    this.loading = true;
    this.error = '';
    this.hubConfiguration = null;

    this.installationService.getHubConfiguration(this.hubId).subscribe({
      next: (data:Hub) => {
        // Filter out null registers if they exist
        if (data && data.installation && Array.isArray(data.installation.registers)) {
          data.installation.registers = data.installation.registers.filter((register: Register) => register !== null);
        }

        this.hubConfiguration = data;
        this.loading = false;
        console.log('Hub configuration:', this.hubConfiguration);
      },
      error: (err) => {
        this.error = `Error loading hub configuration: ${err.message || 'Unknown error'}`;
        this.loading = false;
        console.error('Error loading hub configuration:', err);
      }
    });
  }

  /**
   * Get the voltage value for a register based on its index
   * @param index The index of the register
   * @returns The voltage value, or 'N/A' if not available
   */
  getVoltage(index: number): string {
    if (!this.wsData) {
      return 'N/A';
    }
    const key = `v${index}`;
    return this.wsData[key] !== undefined ? this.wsData[key].toFixed(6) : 'N/A';
  }

  /**
   * Get the frequency value for a register based on its index
   * @param index The index of the register
   * @returns The frequency value, or 'N/A' if not available
   */
  getFrequency(index: number): string {
    if (!this.wsData) {
      return 'N/A';
    }
    const key = `f${index}`;
    return this.wsData[key] !== undefined ? this.wsData[key].toFixed(2) : 'N/A';
  }


  getEnergyPercent(index: number){
    if (!this.wsData) {
      return 0;
    }
    const key = `e${index}`;
    const val = this.wsData[key] !== undefined ? this.wsData[key].toFixed(6) :.000001;
    return (val/Math.abs(this.sumE)) * 100;
  }

  /**
   * Get the power value for a register based on its index
   * @param index The index of the register
   * @returns The power value, or 'N/A' if not available
   */
  getPower(index: number): string {
    if (!this.wsData) {
      return 'N/A';
    }
    const key = `p${index}`;
    return this.wsData[key] !== undefined ? this.wsData[key].toFixed(6) : 'N/A';
  }

  getPowerPercent(index: number){
    if (!this.wsData) {
      return 0;
    }
    const key = `p${index}`;
    const val = this.wsData[key] !== undefined ? this.wsData[key].toFixed(6) :.000001;
    return (val/Math.abs(this.sumP)) * 100;
  }

  /**
   * Get the energy value for a register based on its index
   * @param index The index of the register
   * @returns The energy value, or 'N/A' if not available
   */
  getEnergy(index: number): string {
    if (!this.wsData) {
      return 'N/A';
    }
    const key = `e${index}`;
    return this.wsData[key] !== undefined ? this.wsData[key].toFixed(6) : 'N/A';
  }

  /**
   * Get the current value for a register based on its index
   * @param index The index of the register
   * @returns The current value, or 'N/A' if not available
   */
  getCurrent(index: number): string {
    if (!this.wsData) {
      return 'N/A';
    }
    const key = `i${index}`;
    return this.wsData[key] !== undefined ? this.wsData[key].toFixed(6) : 'N/A';
  }

  /**
   * Get the power factor value for a register based on its index
   * @param index The index of the register
   * @returns The power factor value, or 'N/A' if not available
   */
  getPowerFactor(index: number): string {
    if (!this.wsData) {
      return 'N/A';
    }
    const key = `pf${index}`;
    return this.wsData[key] !== undefined ? this.wsData[key].toFixed(3) : 'N/A';
  }

  /**
   * Format the last data received time as a human-readable date/time string
   * @returns The formatted date/time string, or 'N/A' if no data has been received
   */
  getLastDataReceivedTimeFormatted(): string {
    if (!this.lastDataReceivedTime) {
      return 'N/A';
    }
    // Convert Unix timestamp (seconds) to JavaScript Date object (milliseconds)
    const date = new Date(this.lastDataReceivedTime * 1000);
    // Return formatted date/time string
    // return date.toLocaleString();
    return date.toISOString() + " / " + date.toLocaleString();
  }

  getCircuitTypeLabel(index:number):string | undefined{

    if (!this.hubConfiguration) {
      return "Unknown";
    }

    const register = this.hubConfiguration.installation.registers[index];

    return getRegisterTypeLabel(register);

  }

  getCircuitDisplayClass(index:number):string | undefined{

    if (!this.hubConfiguration) {
      return "badge bg-danger me-1";
    }

    const register = this.hubConfiguration.installation.registers[index];

    return getRegisterTypeDisplayClass(register);
  }


  /**
   * Start editing a register (full edit mode)
   * @param index The index of the register to edit
   */
  startEditingRegister(index: number): void {
    if (!this.hubConfiguration || !this.hubConfiguration.installation || !this.hubConfiguration.installation.registers) {
      return;
    }

    const register = this.hubConfiguration.installation.registers[index];
    if (!register) {
      return;
    }

    this.editMode = true;
    this.editingRegisterIndex = index;
    this.editingRegister = { ...register }; // Create a copy of the register

    // Set temporary values for editing
    this.editingCircuitType = getRegisterTypeId(register);

    this.editingClampDefinition = register.clamp_definition;
    this.editingMultiplier = register.multiplier;
    this.editingInverted = register.inverted;
    this.editingRegisterLabel = register.label; // Also set the label for editing

    this.registerError = '';
  }

  /**
   * Cancel editing a register (full edit mode)
   */
  cancelEditingRegister(): void {
    this.editMode = false;
    this.editingRegisterIndex = null;
    this.editingRegister = null;
    this.registerError = '';
  }

  /**
   * Save the edited register (full edit mode)
   */
  async saveRegister() {
    if (!this.hubConfiguration || !this.hubId || this.editingRegisterIndex === null || !this.editingRegister) {
      return;
    }

    this.savingRegister = true;
    this.registerError = '';

    const clampId = this.clampDefinitionService.getClampDefinitionId(this.editingClampDefinition);
    const circuitFlags = getRegisterFlags(this.editingCircuitType);

    try {

      const body = {
        grid: circuitFlags.grid,
        production: circuitFlags.production,
        battery: circuitFlags.battery,
        clamp_definition: clampId,
        multiplier: this.editingMultiplier,
        inverted: this.editingInverted,
        label: this.editingRegisterLabel
      };

      const response = await this.installationService.updateRegister(this.hubId, this.editingRegisterIndex, body);

      if (response) {
        if (this.hubConfiguration && this.hubConfiguration.installation &&
          this.hubConfiguration.installation.registers && this.editingRegisterIndex !== null) {

          this.hubConfiguration.installation.registers[this.editingRegisterIndex].grid = circuitFlags.grid;
          this.hubConfiguration.installation.registers[this.editingRegisterIndex].production = circuitFlags.production;
          this.hubConfiguration.installation.registers[this.editingRegisterIndex].battery = circuitFlags.battery;

          this.hubConfiguration.installation.registers[this.editingRegisterIndex].clamp_definition = this.editingClampDefinition;
          this.hubConfiguration.installation.registers[this.editingRegisterIndex].multiplier = this.editingMultiplier;
          this.hubConfiguration.installation.registers[this.editingRegisterIndex].inverted = this.editingInverted;
          this.hubConfiguration.installation.registers[this.editingRegisterIndex].label = this.editingRegisterLabel;
        }

        this.editMode = false;
        this.editingRegisterIndex = null;
        this.editingRegister = null;
        this.savingRegister = false;
        return;
      } else {
        this.registerError = `Error updating register`;
        this.savingRegister = false;
      }
    } catch (err: any) {
      this.registerError = `Error updating register: ${err.message || 'Unknown error'} + ${err.error.message || ''} `;
      this.savingRegister = false;
      console.error('Error updating register:', err);
    }
  }

  /**
   * Determines if the Inverted field should be editable based on the RegisterType
   * @param index The index of the register
   * @returns True if the Inverted field should be editable, false otherwise
   */
  isInvertedFieldEditable(index: number): boolean {
    // If we're in edit mode and this is the register being edited, use the editingCircuitType
    if (this.editMode && this.editingRegisterIndex === index) {
      // Allow editing only if RegisterType is "Main" or "Battery"
      return this.editingCircuitType === RegisterTypeEnum.Main || this.editingCircuitType === RegisterTypeEnum.Battery;
    }

    // Otherwise, use the register from hubConfiguration
    if (!this.hubConfiguration || !this.hubConfiguration.installation || !this.hubConfiguration.installation.registers) {
      return false;
    }

    const register = this.hubConfiguration.installation.registers[index];
    if (!register) {
      return false;
    }

    const registerTypeId = getRegisterTypeId(register);

    // Allow editing only if RegisterType is "Main" or "Battery"
    return registerTypeId === RegisterTypeEnum.Main || registerTypeId === RegisterTypeEnum.Battery;
  }

  getClampDefinitions(registerIndex:number){
    console.log(`get clamps defs for reg ${registerIndex}`);
    const clampDefs = this.clampDefinitionService.getAllClampDefinitions();

    if(this.getIsRevenueGrade() && registerIndex < 4){

      return clampDefs.filter((clampDef: ClampDefinition) => clampDef.id === 'XIAMEN100');

    } else {

      if(this.hubConfiguration && this.hubConfiguration.model_number && this.hubConfiguration.model_number.startsWith('00701')) {

        if ((registerIndex < 4)) {
          // high accuracy
          return clampDefs.filter((clampDef: ClampDefinition) => clampDef.hardware_family.mini["high-accuracy"].w !== null);

        } else {
          // standard-accuracy
          return clampDefs.filter((clampDef: ClampDefinition) => clampDef.hardware_family.mini["standard-accuracy"].w !== null);

        }
      } else {
        // hub model is not a mini
        return clampDefs.filter((clampDef: ClampDefinition) => clampDef.hardware_family.standard.w !== null);

      }

    }

  }

  getCircuitTypesFiltered(registerIndex:number) {

    return getRegisterTypes();
  }

  getIsRevenueGrade(){
    return !!(this.hubConfiguration && this.hubConfiguration.model_number && this.hubConfiguration.model_number.endsWith('-RVG'));
  }

  protected readonly Math = Math;
}
